<script setup lang="ts">
import type { MetaPage } from '../../models/watchlist.types'

import { useI18n } from '@/shared/i18n'
import { Button, Icon, Input } from '@/shared/ui'

defineProps<{
  modelValue: string
  suggestions: MetaPage[]
  disabled: boolean
  errorKey: string | null
}>()
const emit = defineEmits<{
  'update:modelValue': [value: string]
  add: [page?: MetaPage]
}>()
const { t } = useI18n()
</script>

<template>
  <div class="meta-search grid gap-xs">
    <div class="meta-search__control grid grid-cols-[minmax(0,_1fr)_auto] items-end gap-xs">
      <Input
        :model-value="modelValue"
        :label="t('workspaceDefinitions.watchlist.meta.searchLabel')"
        :placeholder="t('workspaceDefinitions.watchlist.meta.searchPlaceholder')"
        size="lg"
        :disabled="disabled"
        @update:model-value="emit('update:modelValue', String($event))"
        @keydown.enter.prevent="emit('add')"
      >
        <template #start><Icon name="search" :size="15" /></template>
      </Input>
      <Button
        variant="soft"
        size="xl"
        square
        :disabled="disabled || !modelValue.trim()"
        :aria-label="t('workspaceDefinitions.watchlist.meta.action.add')"
        data-testid="meta-add-page"
        @click="emit('add')"
      >
        <Icon name="plus" :size="14" />
      </Button>
    </div>
    <div
      v-if="modelValue.trim() && suggestions.length"
      class="meta-search__suggestions max-h-40 overflow-auto border border-border rounded-md bg-surface"
      role="listbox"
    >
      <button
        v-for="page in suggestions"
        :key="page.page_id"
        type="button"
        role="option"
        class="meta-search__suggestion grid w-full py-xs px-md text-start text-fg [&:hover]:bg-surface-sunken"
        @click="emit('add', page)"
      >
        <strong>{{ page.page_name }}</strong>
        <span>{{ page.page_id }}</span>
      </button>
    </div>
    <p v-if="errorKey" class="meta-search__error text-muted text-[0.75rem] m-0 text-danger">
      {{ t(errorKey) }}
    </p>
  </div>
</template>

<style scoped>
.meta-search .meta-search__suggestion span {
  color: var(--ui-color-muted);
  font-size: 0.75rem;
}
</style>
